import React, { useState } from 'react';
import { ContactFormData, validateForm, sendFormData, formatPhoneNumber } from '../utils/formHandler';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Formatear teléfono automáticamente
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar formulario
    const validation = validateForm(formData);
    if (!validation.isValid) {
      setSubmitStatus('error');
      setSubmitMessage('Por favor, corrige los siguientes errores:\n' + validation.errors.join('\n'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      // Intentar enviar con el servicio de email
      const result = await sendFormData(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage(result.message);
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.message);
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      setSubmitStatus('error');
      setSubmitMessage('Error inesperado. Por favor, intenta nuevamente o contacta directamente a openwebstudios@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="relative w-full min-h-screen overflow-hidden bg-gradient-to-b from-[#000] to-black text-white flex flex-col items-center justify-center px-4 -mt-px">
      {/* Elementos de fondo específicos de contacto - Más notorios */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-10 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/2 right-10 w-80 h-80 bg-pink-500/15 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '1.5s'}}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 animate-slide-up">
            ¡Hablemos de tu <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-600 bg-clip-text text-transparent animate-gradient">proyecto</span>!
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-slide-down">
            Cuéntanos sobre tu idea y te ayudaremos a hacerla realidad
          </p>
        </div>
        
        <div className="max-w-2xl mx-auto animate-scale-in">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300">
            {/* Mensaje de estado */}
            {submitStatus !== 'idle' && (
              <div className={`mb-6 p-4 rounded-xl ${
                submitStatus === 'success' 
                  ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
                  : 'bg-red-500/20 border border-red-500/30 text-red-300'
              }`}>
                <div className="flex items-center space-x-2">
                  {submitStatus === 'success' ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className="font-medium">
                    {submitStatus === 'success' ? '¡Éxito!' : 'Error'}
                  </span>
                </div>
                <p className="mt-2 text-sm whitespace-pre-line">{submitMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="animate-slide-up" style={{animationDelay: '0.2s'}}>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Nombre *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-all duration-300 hover:scale-105"
                    placeholder="Tu nombre"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="animate-slide-up" style={{animationDelay: '0.3s'}}>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Apellido *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-all duration-300 hover:scale-105"
                    placeholder="Tu apellido"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="animate-slide-up" style={{animationDelay: '0.4s'}}>
                <label className="block text-gray-300 text-sm font-medium mb-2">Teléfono</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-all duration-300 hover:scale-105"
                  placeholder="+54 9 11 1234-5678"
                  disabled={isSubmitting}
                />
              </div>
              <div className="animate-slide-up" style={{animationDelay: '0.5s'}}>
                <label className="block text-gray-300 text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-all duration-300 hover:scale-105"
                  placeholder="tu@email.com"
                  disabled={isSubmitting}
                />
              </div>
              <div className="animate-slide-up" style={{animationDelay: '0.6s'}}>
                <label className="block text-gray-300 text-sm font-medium mb-2">Mensaje *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-all duration-300 hover:scale-105 resize-none"
                  placeholder="Cuéntanos sobre tu proyecto..."
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-8 py-4 rounded-xl font-semibold shadow-lg transition-all duration-300 ${
                  isSubmitting
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-xl hover:shadow-blue-500/25 hover:scale-105 animate-pulse hover:animate-none'
                }`}
                style={{animationDelay: '0.8s'}}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Enviando...</span>
                  </div>
                ) : (
                  'Enviar Mensaje'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 